import type { EditorNodeData, NodePlugin } from "./editor-types";

class ComponentRegistry {
  private readonly plugins = new Map<string, NodePlugin>();
  private readonly listeners = new Set<() => void>();

  register<TData extends EditorNodeData>(plugin: NodePlugin<TData>): () => void {
    this.assertValid(plugin);
    if (this.plugins.has(plugin.type)) {
      throw new Error(`Component type \"${plugin.type}\" is already registered.`);
    }
    this.plugins.set(plugin.type, plugin as unknown as NodePlugin);
    this.emit();
    return () => this.unregister(plugin.type);
  }

  replace<TData extends EditorNodeData>(plugin: NodePlugin<TData>): void {
    this.assertValid(plugin);
    this.plugins.set(plugin.type, plugin as unknown as NodePlugin);
    this.emit();
  }

  unregister(type: string): void {
    if (this.plugins.delete(type)) this.emit();
  }

  get(type: string): NodePlugin | undefined {
    return this.plugins.get(type);
  }

  list(): NodePlugin[] {
    return Array.from(this.plugins.values());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private assertValid<TData extends EditorNodeData>(
    plugin: Pick<NodePlugin<TData>, "type" | "declarations" | "documentWindow">,
  ): void {
    if (!plugin.type.trim()) {
      throw new Error("Component type must not be empty.");
    }
    if (typeof plugin.declarations?.interaction?.hasBuiltInDragHandle !== "boolean") {
      throw new Error(
        `Component type "${plugin.type}" must declare interaction.hasBuiltInDragHandle as a boolean.`,
      );
    }
    const creatable = plugin.declarations.availability?.creatable;
    if (creatable !== undefined && typeof creatable !== "boolean") {
      throw new Error(`Component type "${plugin.type}" must declare availability.creatable as a boolean.`);
    }
    if (plugin.documentWindow) {
      const { defaultSize, minSize, Renderer } = plugin.documentWindow;
      const validSize = (size: { width: number; height: number } | undefined) =>
        Boolean(size && Number.isFinite(size.width) && size.width > 0 && Number.isFinite(size.height) && size.height > 0);
      if (!Renderer || !validSize(defaultSize) || (minSize !== undefined && !validSize(minSize))) {
        throw new Error(`Component type "${plugin.type}" has an invalid documentWindow definition.`);
      }
    }
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const componentRegistry = new ComponentRegistry();

/** Public extension point for future first-party or third-party canvas components. */
export function registerKimnoteComponent<TData extends EditorNodeData>(
  plugin: NodePlugin<TData>,
): () => void {
  return componentRegistry.register(plugin);
}
